import { useState, useRef, useEffect } from "react";
import { Alert } from "react-native";
import { mixpanel } from "../../../../../../utils/mixpanel";
import { VoiceNote, Patient, UseAudioPlayerReturn } from "../types/VoiceNotes";
import { TSession } from "../../../../../../utils/types";

export const useAudioPlayer = (
  patient: Patient,
  session: TSession,
): UseAudioPlayerReturn => {
  const [playingNoteId, setPlayingNoteId] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;

    // Stop audio when component unmounts
    return () => {
      isMountedRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayingNoteId(null);
      }
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPlayingNoteId(null);
    }
  };

  // Expose this to global scope for stopping audio globally
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.stopVoiceNotesAudio = stopAudio;
    }

    return () => {
      if (typeof window !== "undefined") {
        delete window.stopVoiceNotesAudio;
      }
    };
  }, []);

  const handlePlayPause = async (note: VoiceNote): Promise<void> => {
    // If already playing this note, pause it
    if (playingNoteId === note.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayingNoteId(null);
      }

      // Track pause action
      mixpanel.track("Voice Note Playback Paused", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
      });

      return;
    }

    // Stop previous audio
    if (audioRef.current) {
      audioRef.current.pause();
    }

    if (!note.url) {
      Alert.alert("Error", "No audio available for this note");
      return;
    }

    // Play the audio
    try {
      const audio = new Audio(note.url);
      audioRef.current = audio;

      audio.onerror = (e) => {
        console.error("Audio element error:", e);
        Alert.alert("Audio Error", "Failed to play audio");
        setPlayingNoteId(null);
      };

      audio.onended = () => {
        setPlayingNoteId(null);

        // Track playback completion
        mixpanel.track("Voice Note Playback Completed", {
          "User Email": session?.userEmail,
          "Clinic Name": session?.clinicName,
          "Patient ID": patient.clinicOSUUID,
          "Patient Name": patient.name,
          "Note ID": note.id,
        });
      };

      // Track play action
      mixpanel.track("Voice Note Playback Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      await audio.play();
      setPlayingNoteId(note.id);
    } catch (error) {
      console.error("Error playing audio:", error);
      Alert.alert("Error", "Failed to play audio");
      setPlayingNoteId(null);
    }
  };

  return {
    playingNoteId,
    loadingAudio,
    handlePlayPause,
    stopAudio,
    setLoadingAudio,
  };
};
